import _ from 'lodash'

import {
  IRejectionValue,
  IS_REJECTED_UI_ERROR,
  hasFieldValue,
  isRequiredEmpty,
} from './fields/modules/ui2-editable-field/ui2-editable-field.internal'
import { hasFieldErrors } from './forms/ui2-form-section/error-reporter'
import { UI2RendererMeta, UI2_RENDERER_META } from './ui2-renderer-meta'

/**
 * Pure UI2 validation helpers extracted from the entity renderer so the shared
 * core renderer carries only a single import of UI2 logic rather than inlining
 * it. These feed the renderer's error reporting / wizard-gating; see
 * ui2-architecture.md.
 *
 * Each takes the already-materialized component props (what the renderer hands
 * a mounted field), never raw schema.
 */

export function getIsolatesChildValueScope(type?: string): boolean | undefined {
  return UI2_RENDERER_META[type ?? '']?.isolatesChildValueScope
}

export function isFieldRequiredEmpty(componentProps: any, meta?: UI2RendererMeta): boolean {
  const customIsEmpty = meta?.isValueEmptyForRequired
  return isRequiredEmpty({
    ...componentProps,
    isValueEmpty: customIsEmpty ? (v) => customIsEmpty(v, componentProps) : undefined,
  })
}

// True when a rejectionPath points at a still-unresolved rejection (value
// empty or unchanged from the rejected original). Matches the field shell's
// getEffectiveErrors() so reporter and save-gate agree.
export function isFieldRejectionUnresolved(componentProps: any): boolean {
  const { rejectionPath, frames, value, isReadOnly, isDisabled } = componentProps ?? {}
  if (!rejectionPath || !frames || isReadOnly || isDisabled) return false
  const rejection = frames.getValue(rejectionPath) as IRejectionValue | undefined
  if (rejection?.status !== 'rejected') return false
  return !hasFieldValue(value) || _.isEqual(value, rejection.originalValue)
}

// Wizard auto-advance fill-gate: is this bound field empty? Broader than
// isFieldRequiredEmpty — gates optional fields too. Read-only/disabled never
// count (user can't fill them). Returns undefined for non-bound components so
// the reporter skips the fill signal entirely.
export function isFieldUnfilledForGate(componentProps: any, meta?: UI2RendererMeta): boolean | undefined {
  const valuePath = componentProps?.frames?.getContext?.('valuePath')
  const isBound = Array.isArray(valuePath) ? valuePath.length > 0 : !!valuePath
  if (!isBound) return undefined
  const { isReadOnly, isEditable, disabled, value } = componentProps ?? {}
  if (isReadOnly || isEditable === false || disabled) return false
  const customIsEmpty = meta?.isValueEmptyForRequired
  if (customIsEmpty) return customIsEmpty(value, componentProps)
  return _.isNil(value) || value === '' || (Array.isArray(value) && value.length === 0)
}

export function resolveFieldValidation(
  componentProps: any,
  meta?: UI2RendererMeta,
): { hasError: boolean; isUnfilled: boolean | undefined } {
  const getCountErrors = meta?.getCountErrors
  return {
    hasError:
      hasFieldErrors(componentProps.errors) ||
      isFieldRequiredEmpty(componentProps, meta) ||
      isFieldRejectionUnresolved(componentProps) ||
      !_.isEmpty(getCountErrors?.(componentProps.value, componentProps)),
    isUnfilled: isFieldUnfilledForGate(componentProps, meta),
  }
}
